const pickFirst = (obj, keys) => {
  for (const key of keys) {
    if (obj && obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return undefined;
};

const toStringSafe = (value, fallback = '') => {
  if (value === undefined || value === null) return fallback;
  const normalized = String(value).trim();
  return normalized || fallback;
};

const toNumberSafe = (value, fallback = null) => {
  if (value === undefined || value === null || value === '') return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toBooleanSafe = (value) => Boolean(value);

const normalizeLetterStatuses = (value) => {
  if (!Array.isArray(value)) return null;
  const statuses = value
    .map((entry) => toStringSafe(entry).toLowerCase())
    .filter((entry) => entry === 'correct' || entry === 'present' || entry === 'absent');
  return statuses.length > 0 ? statuses : null;
};

export const normalizeCustomGameCreate = (raw) => ({
  gameId: toStringSafe(pickFirst(raw, ['gameId', 'game_id', 'id'])),
  slug: toStringSafe(pickFirst(raw, ['slug'])),
  shareUrl: toStringSafe(pickFirst(raw, ['shareUrl', 'share_url'])),
  coins: toNumberSafe(pickFirst(raw, ['coins'])),
  cost: toNumberSafe(pickFirst(raw, ['cost'])),
});

export const normalizeCustomGamePublic = (raw) => ({
  gameId: toStringSafe(pickFirst(raw, ['gameId', 'game_id', 'id'])),
  slug: toStringSafe(pickFirst(raw, ['slug'])),
  style: toStringSafe(pickFirst(raw, ['style'])),
  maxGuesses: toNumberSafe(pickFirst(raw, ['maxGuesses', 'max_guesses'])),
  clueText: toStringSafe(pickFirst(raw, ['clueText', 'clue_text'])),
  hasClue: toBooleanSafe(pickFirst(raw, ['hasClue', 'has_clue'])),
  clueRevealRow: toNumberSafe(pickFirst(raw, ['clueRevealRow', 'clue_reveal_row'])),
  fullZoomOutRow: toNumberSafe(pickFirst(raw, ['fullZoomOutRow', 'full_zoom_out_row']), 4),
  wordLength: toNumberSafe(pickFirst(raw, ['wordLength', 'word_length'])),
  imageUrl: toStringSafe(pickFirst(raw, ['imageUrl', 'image_url'])),
  zoomOriginX: toNumberSafe(pickFirst(raw, ['zoomOriginX', 'zoom_origin_x']), 0.5),
  zoomOriginY: toNumberSafe(pickFirst(raw, ['zoomOriginY', 'zoom_origin_y']), 0.5),
  zoomRadius: toNumberSafe(pickFirst(raw, ['zoomRadius', 'zoom_radius']), 0.35),
  createdByName: toStringSafe(pickFirst(raw, ['createdByName', 'created_by_name']), ''),
});

export const normalizeCustomRunStart = (raw) => ({
  runId: toStringSafe(pickFirst(raw, ['runId', 'run_id', 'id'])),
  status: toStringSafe(pickFirst(raw, ['status']), 'active'),
  gameId: toStringSafe(pickFirst(raw, ['gameId', 'game_id'])),
  slug: toStringSafe(pickFirst(raw, ['slug'])),
  wordLength: toNumberSafe(pickFirst(raw, ['wordLength', 'word_length'])),
  maxGuesses: toNumberSafe(pickFirst(raw, ['maxGuesses', 'max_guesses'])),
  style: toStringSafe(pickFirst(raw, ['style'])),
  clueRevealRow: toNumberSafe(pickFirst(raw, ['clueRevealRow', 'clue_reveal_row'])),
  fullZoomOutRow: toNumberSafe(pickFirst(raw, ['fullZoomOutRow', 'full_zoom_out_row']), 4),
  clueText: toStringSafe(pickFirst(raw, ['clueText', 'clue_text']), ''),
  zoomOriginX: toNumberSafe(pickFirst(raw, ['zoomOriginX', 'zoom_origin_x']), 0.5),
  zoomOriginY: toNumberSafe(pickFirst(raw, ['zoomOriginY', 'zoom_origin_y']), 0.5),
  zoomRadius: toNumberSafe(pickFirst(raw, ['zoomRadius', 'zoom_radius']), 0.35),
});

export const normalizeCustomGuess = (raw) => ({
  runId: toStringSafe(pickFirst(raw, ['runId', 'run_id'])),
  percentage: toNumberSafe(pickFirst(raw, ['percentage']), 0),
  isCorrect: toBooleanSafe(pickFirst(raw, ['isCorrect', 'is_correct'])),
  guessesUsed: toNumberSafe(pickFirst(raw, ['guessesUsed', 'guesses_used']), 0),
  status: toStringSafe(pickFirst(raw, ['status']), 'active'),
  wordLength: toNumberSafe(pickFirst(raw, ['wordLength', 'word_length'])),
  maxGuesses: toNumberSafe(pickFirst(raw, ['maxGuesses', 'max_guesses'])),
  style: toStringSafe(pickFirst(raw, ['style'])),
  clueRevealRow: toNumberSafe(pickFirst(raw, ['clueRevealRow', 'clue_reveal_row'])),
  fullZoomOutRow: toNumberSafe(pickFirst(raw, ['fullZoomOutRow', 'full_zoom_out_row']), 4),
  clueText: toStringSafe(pickFirst(raw, ['clueText', 'clue_text']), ''),
  revealWord: toStringSafe(pickFirst(raw, ['revealWord', 'reveal_word']), ''),
  letterStatuses: normalizeLetterStatuses(
    pickFirst(raw, ['letterStatuses', 'letter_statuses']),
  ),
});

export const normalizeCustomReveal = (raw) => ({
  runId: toStringSafe(pickFirst(raw, ['runId', 'run_id'])),
  status: toStringSafe(pickFirst(raw, ['status']), 'revealed'),
  word: toStringSafe(pickFirst(raw, ['word', 'revealWord', 'reveal_word'])),
});

export const normalizeCustomAiClue = (raw) => ({
  clueText: toStringSafe(pickFirst(raw, ['clueText', 'clue_text'])),
  coins: toNumberSafe(pickFirst(raw, ['coins'])),
  cost: toNumberSafe(pickFirst(raw, ['cost'])),
  model: toStringSafe(pickFirst(raw, ['model']), ''),
});

export const normalizeCustomUpload = (raw) => ({
  imageUrl: toStringSafe(pickFirst(raw, ['imageUrl', 'image_url'])),
  imageKey: toStringSafe(pickFirst(raw, ['imageKey', 'image_key', 'key'])),
  key: toStringSafe(pickFirst(raw, ['key', 'imageKey', 'image_key'])),
  contentType: toStringSafe(pickFirst(raw, ['contentType', 'content_type'])),
  size: toNumberSafe(pickFirst(raw, ['size']), 0),
});

export const normalizeCreatorCustomGame = (raw) => ({
  gameId: toStringSafe(pickFirst(raw, ['gameId', 'game_id', 'id'])),
  slug: toStringSafe(pickFirst(raw, ['slug'])),
  style: toStringSafe(pickFirst(raw, ['style'])),
  maxGuesses: toNumberSafe(pickFirst(raw, ['maxGuesses', 'max_guesses'])),
  hasClue: toBooleanSafe(pickFirst(raw, ['hasClue', 'has_clue'])),
  clueText: toStringSafe(pickFirst(raw, ['clueText', 'clue_text']), ''),
  clueRevealRow: toNumberSafe(pickFirst(raw, ['clueRevealRow', 'clue_reveal_row'])),
  fullZoomOutRow: toNumberSafe(pickFirst(raw, ['fullZoomOutRow', 'full_zoom_out_row']), 4),
  wordLength: toNumberSafe(pickFirst(raw, ['wordLength', 'word_length'])),
  imageUrl: toStringSafe(pickFirst(raw, ['imageUrl', 'image_url'])),
  imageKey: toStringSafe(pickFirst(raw, ['imageKey', 'image_key'])),
  zoomOriginX: toNumberSafe(pickFirst(raw, ['zoomOriginX', 'zoom_origin_x']), 0.5),
  zoomOriginY: toNumberSafe(pickFirst(raw, ['zoomOriginY', 'zoom_origin_y']), 0.5),
  zoomRadius: toNumberSafe(pickFirst(raw, ['zoomRadius', 'zoom_radius']), 0.35),
  createdAt: toStringSafe(pickFirst(raw, ['createdAt', 'created_at'])),
});

export const normalizeCreatorCustomGamesList = (raw) => ({
  games: Array.isArray(raw?.games)
    ? raw.games.map((entry) => normalizeCreatorCustomGame(entry))
    : [],
  limit: toNumberSafe(pickFirst(raw, ['limit']), 50),
  offset: toNumberSafe(pickFirst(raw, ['offset']), 0),
});

export const normalizeCustomZoomUpdate = (raw) => ({
  gameId: toStringSafe(pickFirst(raw, ['gameId', 'game_id'])),
  zoomOriginX: toNumberSafe(pickFirst(raw, ['zoomOriginX', 'zoom_origin_x']), 0.5),
  zoomOriginY: toNumberSafe(pickFirst(raw, ['zoomOriginY', 'zoom_origin_y']), 0.5),
  zoomRadius: toNumberSafe(pickFirst(raw, ['zoomRadius', 'zoom_radius']), 0.35),
  clueRevealRow: toNumberSafe(pickFirst(raw, ['clueRevealRow', 'clue_reveal_row']), 1),
  clueText: toStringSafe(pickFirst(raw, ['clueText', 'clue_text']), ''),
  fullZoomOutRow: toNumberSafe(pickFirst(raw, ['fullZoomOutRow', 'full_zoom_out_row']), 4),
});

export const normalizeCustomDelete = (raw) => ({
  deleted: toBooleanSafe(pickFirst(raw, ['deleted'])),
  gameId: toStringSafe(pickFirst(raw, ['gameId', 'game_id'])),
  slug: toStringSafe(pickFirst(raw, ['slug'])),
});
